import { Component } from 'react';
import * as PropTypes from 'prop-types';
import AnimateHeight from 'react-animate-height';
import classnames from 'classnames/bind';
import { MdExpandLess as ArrowIcon } from 'react-icons/md';
import Label from '../Label';
import styles from './CollapsibleContent.module.scss';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const cln = classnames.bind(styles);
const compose = (...fns) => (...args) => fns.forEach(fn => fn && fn(...args));

/** This component can be controlled by providing expanded property. If not provided then it handles items visibility through an internal state */
class CollapsibleContent extends Component {
  static propTypes = {
    /** @ignore */
    children: PropTypes.node.isRequired,
    /** Optional className to customize the container */
    className: PropTypes.string,
    /** Label of the container */
    label: PropTypes.string,
    /** Indicates whether the content should be visible */
    expanded: PropTypes.bool,
    /** Set initial expanded state and still leave state handling for this component itself (it only works in uncontrolled mode) */
    startExpanded: PropTypes.bool,
    /** Function to be invoked on header click */
    onToggle: PropTypes.func,
    /** This component can be used in two variants */
    variant: PropTypes.oneOf(['expandable', 'cover']),
    /** Optional className to customize the container in expanded state */
    expandedClassName: PropTypes.string,
    /** Optional className to customize the header container */
    headerClassName: PropTypes.string,
    /** Optional className to customize the content container */
    contentClassName: PropTypes.string,
    /** Optional custom header */
    customHeader: PropTypes.element,
    /** With this prop you can hide the arrow icon */
    hideArrowIcon: PropTypes.bool,
    /** Optional className to customize the arrow icon */
    arrowIconClassName: PropTypes.string,
    /** Expanding animation duration in ms (default 100 ms) */
    animationDuration: PropTypes.number
  };
  static defaultProps = {
    className: undefined,
    label: '',
    startExpanded: false,
    expanded: undefined,
    variant: 'expandable',
    expandedClassName: undefined,
    headerClassName: undefined,
    contentClassName: undefined,
    customHeader: undefined,
    hideArrowIcon: false,
    arrowIconClassName: undefined,
    animationDuration: 100,
    onToggle: () => {}
  };
  state = {
    expanded: this.props.startExpanded
  };
  onToggle = () => {
    if (!this.isExpandControlled()) {
      this.setState(({
        expanded
      }) => ({
        expanded: !expanded
      }));
    }
  };
  isExpandControlled = () => this.props.expanded !== undefined;
  render() {
    const {
      children,
      label,
      className,
      onToggle,
      expanded,
      startExpanded,
      // eslint-disable-line no-unused-vars
      variant,
      expandedClassName,
      headerClassName,
      contentClassName,
      customHeader,
      hideArrowIcon,
      arrowIconClassName,
      animationDuration,
      ...rest
    } = this.props;
    const isExpanded = this.isExpandControlled() ? expanded : this.state.expanded;
    const containerClasses = cln(className, 'CollapsibleContent', `CollapsibleContent--${variant}`, {
      'CollapsibleContent--customHeader': customHeader,
      'CollapsibleContent--expanded': isExpanded,
      [expandedClassName || `${className}--expanded`]: isExpanded && (expandedClassName || className)
    });
    return /*#__PURE__*/_jsxs("div", {
      className: containerClasses,
      ...rest,
      children: [/*#__PURE__*/_jsx("header", {
        "data-testid": "cc-header",
        className: cln('Header', headerClassName),
        onClick: compose(onToggle, this.onToggle),
        children: variant === 'cover' ? customHeader || /*#__PURE__*/_jsx("span", {
          className: cln('Label'),
          children: label
        }) : /*#__PURE__*/_jsxs(_Fragment, {
          children: [customHeader || /*#__PURE__*/_jsx(Label, {
            className: cln('Label'),
            children: label
          }), !hideArrowIcon && /*#__PURE__*/_jsx(ArrowIcon, {
            className: cln('ArrowIcon', arrowIconClassName)
          })]
        })
      }), /*#__PURE__*/_jsx("section", {
        "data-testid": "cc-content",
        className: cln('Content', contentClassName),
        children: /*#__PURE__*/_jsx(AnimateHeight, {
          duration: animationDuration,
          height: isExpanded ? 'auto' : 0,
          children: children
        })
      })]
    });
  }
}
export default CollapsibleContent;